import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { HiddenLensOptions, LensSkus, PRESCRIPTION_TYPES, PRODUCT_TYPES } from '@utils/enums';
import { deleteBundle, checkForLineItem } from '@utils/shopify-helpers';
import { getFirstProductVariantByHandle, getOneRecommendedProduct, getProductByHandle } from '@services/product';
import CartItemsBundle from '@components/cart/receipt/CartItemsBundle';
import { deleteItemsBulk, setPairCareProduct } from '@features/cart/cartSlice';
import CartBundleLoader from '@components/cart/receipt/CartBundleLoader';
import Upsell from '@components/cart/receipt/Upsell';
import styles from './CartBundleGroup.module.scss';
import useAlgoliaRecommendations from '@hooks/useAlgoliaRecommendations';
import { nanoid } from 'nanoid';
import useShopifyRecommendations from '@hooks/useShopifyRecommendations';

const CartBundleGroup = ({ baseItemGroupKey, itemGroup, numberOfBases, groupIndex, isOthersGroup, minicart }) => {
	const dispatch = useDispatch();
	const allItems = useSelector(state => state.cart.items);
	const [itemBundle, setItemBundle] = useState([...itemGroup]);
	const [topUpsell, setTopUpsell] = useState(false);
	const isLoading = useSelector(state => state.cart.isLoading);
	const loadingKey = useSelector(state => state.cart.loadingKey);
	const rootUrl = useSelector(state => state.shop.routes.root_url);
	const [nonRxGroup, setNonRxGroup] = useState(false);
	let hasPairCare = false;
	let pairCareKey = hasPairCare ? itemBundle.find(item => item.sku === 'PAIR_CARE').key : undefined;
	let baseIndex = itemBundle.findIndex(item => item.product_type.includes(PRODUCT_TYPES.BASE_FRAME));
	let lensIndex = itemBundle.findIndex(item => item.product_type.includes(PRODUCT_TYPES.LENS));
	let topsIndex = itemBundle.findIndex(item => item.product_type.includes(PRODUCT_TYPES.TOP_FRAME));
	const headersStyle = `title h2 ${styles.headers}`;

	const basesAndLenses = itemGroup.filter(item => item.product_type.includes(PRODUCT_TYPES.BASE_FRAME) || item.product_type.includes(PRODUCT_TYPES.BASE_FRAME)) ?? [];
	const lastBaseOrLense = basesAndLenses.length ? basesAndLenses[basesAndLenses.length - 1] : null;
	const lastItem = lastBaseOrLense ?? itemGroup[itemGroup.length - 1];
	const alogliaRecs = useAlgoliaRecommendations({ objectID: lastItem?.id?.toString() ?? '' });
	const shopifyRecommendations = useShopifyRecommendations({
		productId: lastItem.product_id.toString(),
		productTitle: lastItem.title,
		selectedBaseFrame: lastItem?.properties?._Frame,
		rootUrl,
	});
	let recommendations = alogliaRecs.length ? alogliaRecs : shopifyRecommendations;

	const rxType = itemBundle[0]?.properties?._prescriptionType;

	if (itemBundle.some(item => item.sku === 'PAIR_CARE')) {
		hasPairCare = true;
		pairCareKey = itemBundle.find(item => {
			return item.sku === 'PAIR_CARE';
		}).key;
	}

	useEffect(() => {
		let rxItem;

		const rxItemSetup = () => {
			const isSingleVision = rxType === PRESCRIPTION_TYPES.SINGLE_VISION;
			const isNonPrescription = rxType === PRESCRIPTION_TYPES.NON_PRESCRIPTION;

			if (!isSingleVision && !isNonPrescription) {
				return;
			}

			rxItem = {
				is_visual: true,
				product_type: PRODUCT_TYPES.LENS,
				properties: {
					_bundle_key: baseItemGroupKey,
				},
				discounted_price: 0,
				quantity: 1,
			};

			if (isSingleVision) {
				const singlePrescriptionIndex = itemGroup.findIndex(item => item.sku.includes(PRESCRIPTION_TYPES.SINGLE_VISION));
				if (singlePrescriptionIndex < 0) {
					rxItem.product_title = PRESCRIPTION_TYPES.SINGLE_VISION.replace('-', ' ');
					rxItem.sku = PRESCRIPTION_TYPES.SINGLE_VISION;
				}
			}

			if (isNonPrescription) {
				const nonPrescriptionIndex = itemGroup.findIndex(item => item.sku.includes(PRESCRIPTION_TYPES.NON_PRESCRIPTION));
				if (nonPrescriptionIndex < 0) {
					rxItem.product_title = PRESCRIPTION_TYPES.NON_PRESCRIPTION.replace('-', ' ');
					rxItem.sku = PRESCRIPTION_TYPES.NON_PRESCRIPTION;
				}
			}
		};

		const fetchUpsellForGroups = async () => {
			baseIndex = itemGroup.findIndex(item => item.product_type.includes(PRODUCT_TYPES.BASE_FRAME));
			lensIndex = itemGroup.findIndex(item => item.product_type.includes(PRODUCT_TYPES.LENS));
			topsIndex = itemGroup.findIndex(item => item.product_type.includes(PRODUCT_TYPES.TOP_FRAME));

			const newItemBundle = await getUpsellsForGroup([...itemGroup]);

			if (rxItem) {
				const newItemsWithRx = [...newItemBundle];
				const indexToAppend = newItemsWithRx.findIndex(item => item.product_type.includes(PRODUCT_TYPES.LENS));
				newItemsWithRx.splice(indexToAppend, 0, rxItem);
				setItemBundle([...newItemsWithRx]);
			} else {
				setItemBundle(newItemBundle);
			}
		};
		rxItemSetup();
		fetchUpsellForGroups();
	}, [itemGroup, recommendations]);

	useEffect(() => {
		const fetchPairCareUpsell = async () => {
			const newPairCareProduct = await getFirstProductVariantByHandle('pair-care');
			dispatch(setPairCareProduct(newPairCareProduct));
		};
		fetchPairCareUpsell();
	}, []);

	async function getUpsellsForGroup(itemGroup) {
		const updatedGroup = [...itemGroup];
		const hasBlueLight = itemGroup.some(item => item.sku === LensSkus.BLUE_LIGHT);
		const hasPremium = itemGroup.some(item => item.sku === LensSkus.ULTRA_THIN);

		const baseProperties = baseIndex + 1 ? itemGroup[baseIndex].properties : false;
		if (baseIndex + 1 && itemGroup[baseIndex]?.properties?._prescriptionType === 'Non-Prescription') {
			setNonRxGroup(true);
		}
		const hiddenLensOptions = baseProperties && baseProperties?._customerJourney && baseProperties?._prescriptionType
			? HiddenLensOptions[baseProperties._customerJourney][baseProperties._prescriptionType]
			: [];
		const baseWithNoBlueLight = !hasBlueLight && baseIndex + 1 && baseProperties._customerJourney !== LensSkus.SUN_LENS;
		const baseWithNoPremium = !hasPremium && baseIndex + 1 && baseProperties._customerJourney !== LensSkus.SUN_LENS;

		for (let i = 0; i < itemGroup.length; i++) {
			const item = itemGroup[i];

			if (
				baseWithNoPremium &&
				!hiddenLensOptions.includes(LensSkus.ULTRA_THIN) &&
				(i + 1 === topsIndex || (topsIndex < 0 && i + 1 === itemGroup.length))
			) {
				const premiumThinProduct = await getProductByHandle('premium-ultra-thin');
				if (premiumThinProduct) {
					const indexToAppend = topsIndex < 1 ? i + 1 : topsIndex;
					premiumThinProduct.upsell = true;
					premiumThinProduct.product_type = PRODUCT_TYPES.LENS;
					updatedGroup.splice(indexToAppend, 0, premiumThinProduct);
				}
			}

			if (
				baseWithNoBlueLight &&
				!hiddenLensOptions.includes(LensSkus.BLUE_LIGHT) &&
				(i + 1 === topsIndex || (topsIndex < 0 && i + 1 === itemGroup.length))
			) {
				const blueLightProduct = await getProductByHandle('blue-light-filtering');
				if (blueLightProduct) {
					const indexToAppend = topsIndex < 1 ? i + 1 : topsIndex;
					blueLightProduct.upsell = true;
					blueLightProduct.product_type = PRODUCT_TYPES.LENS;
					updatedGroup.splice(indexToAppend, 0, blueLightProduct);
				}
			}

			if (
				recommendations.length &&
				item.product_type.includes(PRODUCT_TYPES.TOP_FRAME) &&
				i === itemGroup.length - 1 &&
				!item.upsell
			) {
				const recommendedProduct = await getOneRecommendedProduct(item.product_id, item.variant_title, itemGroup);

				let newRec = recommendations[0];
				let cursor = 1;
				let safeToDisplay = !checkForLineItem(allItems, newRec.id);

				while (cursor < recommendations.length && !safeToDisplay) {
					newRec = recommendations[cursor];
					safeToDisplay = !checkForLineItem(allItems, newRec.id);

					if (safeToDisplay) {
						break;
					}
					cursor++;
				}

				const algoliaRecommendation = {
					...newRec,
					name: newRec.title,
					featured_image: { src: newRec.image },
					available: true,
					title: newRec.option1,
				};

				const safeRec = safeToDisplay ? algoliaRecommendation : recommendedProduct;

				if (safeToDisplay) {
					safeRec.upsell = true;
					safeRec.product_type = PRODUCT_TYPES.TOP_FRAME;
					setTopUpsell(safeRec);
				} else {
					setTopUpsell(false);
				}
			}
		}

		return updatedGroup;
	}

	const onDeleteBaseFrame = async () => {
		const data = {
			updates: {},
		};

		itemGroup.forEach(item => {
			if (!item.upsell) {
				data.updates[item.key] = 0;
			}
		});

		await deleteBundle(data);
		dispatch(deleteItemsBulk({ deletedItems: itemGroup, nonRxGroup }));
	};

	return (
		<>
			<div className='cart-item__wrap' data-bundle={baseItemGroupKey}>
				<CartBundleLoader isActive={isLoading && loadingKey === baseItemGroupKey} />
				{itemBundle.map((item, index) => {
					let titleNode = null;
					if (isOthersGroup && !index) {
						titleNode = <h2 className={headersStyle}>Other Items</h2>;
					} else {
						switch (index) {
							case baseIndex:
								titleNode = (
									<h2 data-test='my-base-frame' className={headersStyle}>
										My Base Frame{numberOfBases > 1 ? ` (${groupIndex + 1}/${numberOfBases})` : ''}
									</h2>
								);
								break;
							case lensIndex:
								titleNode = <h2 className={headersStyle}>My Lenses</h2>;
								break;
							case topsIndex:
								if (isOthersGroup) break;
								titleNode = (
									<h2 data-test='my-top-frame' className={headersStyle}>
										My Top Frames
									</h2>
								);
								break;
							default:
								break;
						}
					}

					return (
						<Fragment key={nanoid()}>
							{titleNode}
							<CartItemsBundle
								item={item}
								hasPairCare={hasPairCare}
								rxType={rxType}
								pairCareKey={pairCareKey}
								nonRxGroup={nonRxGroup}
								onDeleteBaseFrame={onDeleteBaseFrame}
								baseItemGroupKey={baseItemGroupKey}
								minicart={minicart}
							/>
						</Fragment>
					);
				})}
			</div>
			{topUpsell && (
				<div className='cart-item__wrap' data-bundle={baseItemGroupKey}>
					<h2 className={headersStyle}>We think you'd like</h2>
					<Upsell
						bundleKey={baseItemGroupKey}
						addInfo={`The most popular top for The ${topUpsell.title}!`}
						itemGroupKey={baseItemGroupKey}
						nonRxGroup={nonRxGroup}
						lozengeMessage='Top Rated'
						recommendation={topUpsell}
					/>
				</div>
			)}
		</>
	);
};

CartBundleGroup.propTypes = {
	baseItemGroupKey: PropTypes.string,
	itemGroup: PropTypes.any.isRequired,
	numberOfBases: PropTypes.number,
	groupIndex: PropTypes.number,
	isOthersGroup: PropTypes.bool,
	minicart: PropTypes.bool,
};

export default CartBundleGroup;
